import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CumulativeCostDataPoint, ChartColors, defaultColors } from '../types';
import { formatCurrency } from '../utils';

export interface CumulativeCostChartProps {
  data: CumulativeCostDataPoint[];
  colors?: ChartColors;
  className?: string;
}

export const CumulativeCostChart: React.FC<CumulativeCostChartProps> = ({
  data,
  colors = defaultColors,
  className = '',
}) => {
  // Memoize data keys to avoid recalculating on every render
  const dataKeys = React.useMemo(
    () => Object.keys(data[0] || {}).filter((key) => key !== 'year'),
    [data]
  );

  const getLineColor = (key: string, index: number): string => {
    if (key === 'baseline') return colors.baseline;
    if (index === 1) return colors.primary;
    return colors.alternatives[(index - 2) % colors.alternatives.length];
  };

  const formatTooltipValue = (value: number) => formatCurrency(value);

  return (
    <div className={`cumulative-cost-chart ${className}`}>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
          <YAxis
            label={{ value: 'Cumulative Cost (£)', angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip formatter={formatTooltipValue} />
          <Legend />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getLineColor(key, index)}
              strokeWidth={key === 'baseline' ? 2 : 2.5}
              strokeDasharray={key === 'baseline' ? '5 5' : undefined}
              dot={false}
              name={key.charAt(0).toUpperCase() + key.slice(1)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
